import React, { useMemo } from 'react';
import { Grid } from '@material-ui/core';
import yup from '../../../utility/yup';
import { Loadable } from '../../../store/types';
import { Project } from '../../../types/items';
import {
  ItemForm,
  FormInput,
  FieldUserSelect,
  FormInfoInput,
  FieldOrganizationSelect,
} from '../../../components';
import OrganizationField from './fields/OrganizationField';
import OwnerUserField from './fields/OwnerUserField';
import StatusSelect from './fields/StatusSelect';
import { Filter } from '../../../types/main';
import { useGuard } from '../../../hooks';
import { is, roles } from '../../../utility/guard';

const projectSchema = yup.object().shape({
  title: yup.string().required().trim(),
  organization_id: yup.number().typeError('Organization is required').required('Organization is required'),
  user_owner_id: yup.number().typeError('Owner is required').required('Owner is required'),
});

type Props = {
  creatingMode?: boolean;
  record: Loadable<Project>;
  onSubmit: (record: Project) => void;
  getTitle?: (() => string);
};

const ProjectForm = ({
  creatingMode = false,
  record: { loading, data },
  onSubmit,
  getTitle = undefined,
}: Props) => {
  const canEdit = data?.permissions.can_edit || false;
  const isSuperAdmin = useGuard(is(roles.superAdmin));

  const additionalFiltersAssignedToUser: Filter[] = useMemo(() => [
    { key: 'is_super_admin', value: '1' },
  ], []);

  return (
    <>
      <ItemForm
        creatingMode={creatingMode}
        initialValues={data}
        validationSchema={projectSchema}
        onSubmit={onSubmit}
        loading={loading}
        editPermitted={Boolean(data?.permissions.can_edit)}
        enableReinitialize
        preventCancelChanges
        validateOnMount={false}
        validateOnBlur={false}
        validateOnChange
      >
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6}>
            <FormInput
              label="Title"
              name="title"
              autoFocus
              data-intercom-target="Title"
              getValueAfterInit={!loading ? getTitle : undefined}
              disabled={!canEdit}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <StatusSelect disabled={!canEdit} />
          </Grid>
          <Grid item xs={12}>
            <FormInput
              label="Description"
              name="description"
              type="textarea"
              disabled={!canEdit}
              spellCheck
            />
          </Grid>
          {data?.ref.organization?.common_name && !loading
            && (
              <>
                <Grid item xs={6}>
                  {isSuperAdmin
                    ?
                    <FieldOrganizationSelect name={data?.ref.organization?.common_name || ''} />
                    : <OrganizationField project={data} />}
                </Grid>
                <Grid item xs={6}>
                  <OwnerUserField
                    project={data}
                  />
                </Grid>
              </>
            )}
          <Grid item xs={12}>
            <Grid item xs={6}>
              {isSuperAdmin && (
                <FieldUserSelect
                  userId={data?.assigned_to_id}
                  label="Assigned To"
                  name="assigned_to_id"
                  disabled={!isSuperAdmin}
                  additionalFilters={additionalFiltersAssignedToUser}
                  defaultValue={-1}
                />
              )}
              {!isSuperAdmin && (
                <FormInfoInput
                  value={data?.ref.assigned_to?.full_name_first_last || ''}
                  label="Assigned To"
                />
              )}

            </Grid>
          </Grid>
        </Grid>
      </ItemForm>
    </>
  );
};

export default ProjectForm;
